import React, { useEffect, useState } from 'react';

import { uniq } from 'lodash-es';

import Button from '@/components/ui/buttons/Button';
import {
  isCompleteOrganizationMembershipCode,
  ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS,
} from '@/components/ui/formats/organizationMembershipCode';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Input from '@/components/ui/forms/Input';
import LabelWithStatus from '@/components/ui/forms/LabelWithStatus';
import OrganizationMembershipCodeSplitInput from '@/components/ui/forms/OrganizationMembershipCodeSplitInput';
import PasswordInput from '@/components/ui/forms/PasswordInput';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import useReactQuery from '@/hooks/common/useReactQuery';
import useStudentInfo from '@/hooks/registration/useStudentInfo';
import {
  type AccountRegistrationFormValues,
  type StudentInfo,
} from '@/interfaces/registration';

import { createValidationSchema } from '../schema/validationSchema';

import CSSModule from './AccountRegistrationForm.module.scss';

interface Props {
  formValues: AccountRegistrationFormValues;
  onSubmit: (
    formValues: AccountRegistrationFormValues,
    studentInfo: StudentInfo,
  ) => void;
}

const AccountRegistrationForm: React.FC<Props> = ({ formValues, onSubmit }) => {
  const [serverEmailErrorMessages, setServerEmailErrorMessages] = useState<
    (string | React.ReactElement)[]
  >([]);
  const [serverStudentKanaErrorMessages, setServerStudentKanaErrorMessages] =
    useState<string[]>([]);
  // 一度フォーカスを外したあとは、10桁を入力し切るまで必須エラーを表示し続ける
  const [isCodeRequiredErrorActive, setIsCodeRequiredErrorActive] =
    useState(false);
  const { useMutation } = useReactQuery();

  // メールアドレス検証API
  const {
    mutate: validateEmail,
    isPending: isEmailPending,
    error: emailError,
  } = useMutation({
    method: 'POST',
    url: '/api/school/parents/email_validations/validate',
  });

  const {
    registerWithInnerRef,
    handleSubmit,
    getValues,
    resetField,
    watch,
    setValue,
    formState: {
      isValid,
      isSubmitting,
      touchedFields,
      validFields,
      errorMessages,
      dirtyFields,
    },
    trigger,
  } = useReactHookForm<AccountRegistrationFormValues>({
    validationSchema: createValidationSchema(),
    defaultValues: formValues,
  });

  const codeErrors = [...(errorMessages.code || [])];

  // 規定桁数の半角数字が入力されたらOK
  const codeValue = watch('code');
  const cleanedCode = codeValue?.replace(/\s/g, '') || '';
  const isCodeReadyForOk =
    isCompleteOrganizationMembershipCode(cleanedCode) && !codeErrors.length;

  // 分割入力欄には空白が混入しうるため、API には正規化した値を渡す
  const validateCode = cleanedCode;
  const studentFirstNameKanaValue = watch('student_first_name_kana');
  const studentLastNameKanaValue = watch('student_last_name_kana');

  // 生徒情報取得API
  const {
    isLoading: isStudentLoading,
    error: studentError,
    refetch: refetchStudent,
  } = useStudentInfo({
    organizationMembershipCode: validateCode,
    studentFirstNameKana: getValues('student_first_name_kana'),
    studentLastNameKana: getValues('student_last_name_kana'),
  });

  const inputDisabled = isSubmitting || isEmailPending || isStudentLoading;
  // スキーマは桁数を問わないため、送信可否は 10 桁ちょうど入力済みか（isCodeReadyForOk）でも判定する
  const buttonDisabled =
    !isValid ||
    isSubmitting ||
    isEmailPending ||
    isStudentLoading ||
    !isCodeReadyForOk;
  const is500Error = emailError?.status === 500 || studentError?.status === 500;
  const isRateLimitError =
    emailError?.status === 429 || studentError?.status === 429;

  const passwordErrors = uniq([
    ...(errorMessages.password || []),
    ...(errorMessages.password_confirmation || []),
  ]);

  const nameErrors = [
    ...(errorMessages.last_name || []),
    ...(errorMessages.first_name || []),
  ];

  const nameKanaErrors = [
    ...(errorMessages.last_name_kana || []),
    ...(errorMessages.first_name_kana || []),
  ];

  const studentNameKanaErrors = [
    ...(errorMessages.student_last_name_kana || []),
    ...(errorMessages.student_first_name_kana || []),
  ];

  const hasStudentKanaServerMessages =
    serverStudentKanaErrorMessages.length > 0;
  const shouldShowStudentKanaServerError =
    hasStudentKanaServerMessages &&
    !dirtyFields.student_first_name_kana &&
    !dirtyFields.student_last_name_kana;
  const shouldKeepCodeRequired =
    hasStudentKanaServerMessages && !dirtyFields.code;
  const hasTouchedStudentKana = Boolean(
    touchedFields.student_first_name_kana ||
    touchedFields.student_last_name_kana,
  );
  const isStudentKanaReadyForOk =
    hasTouchedStudentKana &&
    Boolean(studentFirstNameKanaValue) &&
    Boolean(studentLastNameKanaValue) &&
    !errorMessages.student_first_name_kana?.length &&
    !errorMessages.student_last_name_kana?.length;

  // フォーカスを外して必須エラーが有効化された後は、規定桁数になるまで「必須です」を表示する
  const showCodeRequiredError =
    isCodeRequiredErrorActive &&
    cleanedCode.length !== ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS;

  // 空欄時はスキーマの必須エラーと showCodeRequiredError が同じ文言を出すため uniq で重複を除去する。
  const filteredCodeErrors = uniq([
    ...codeErrors,
    ...(showCodeRequiredError ? ['団体会員コードは必須です'] : []),
  ]);

  const emailErrors = [...(errorMessages.email || [])];

  const loginMessage: React.ReactElement = (
    <>
      既に同じメールアドレスで for PARENTSを利用している場合は、
      <a
        href="https://studysapuri-school.jp/studentfaq/s/article/000001839"
        target="_blank"
        style={{ textDecoration: 'underline' }}
      >
        よくある質問
      </a>
      を確認して生徒の追加登録を行なってください
    </>
  );

  const CustomEmailErrorMessages: React.FC = () => {
    if (!serverEmailErrorMessages) {
      return;
    }
    // API検証エラーが存在する場合のみ、APIエラーとloginMessageを表示
    return (
      !dirtyFields.email && <ErrorList errors={serverEmailErrorMessages} />
    );
  };

  const ServerErrorMessage: React.FC = () =>
    is500Error && (
      <ErrorList
        errors={[
          '予期しないエラーが発生しました。時間をおいて再度お試しください',
        ]}
      />
    );

  const RateLimitErrorMessage: React.FC = () =>
    isRateLimitError && (
      <ErrorList
        errors={[
          'リクエスト回数の上限に達しました。しばらく経ってから再度お試しください',
        ]}
      />
    );

  useEffect(() => {
    if (is500Error) {
      resetField('password');
      resetField('password_confirmation');
    }
  }, [is500Error, resetField]);

  const submitHandler = handleSubmit(async (values) => {
    // スキーマが桁数を問わないため、Enter キー等で送信ボタンの活性判定（isCodeReadyForOk）を
    // 経由せずに未完成コードのまま到達しうる。送信ボタンと同じ完了判定でガードし、
    // 未完成なら必須エラーを表示して中断する。
    if (!isCodeReadyForOk) {
      setIsCodeRequiredErrorActive(true);
      return;
    }

    setServerEmailErrorMessages([]);
    setServerStudentKanaErrorMessages([]);

    const [emailResults, studentResults] = await Promise.all([
      new Promise<boolean>((resolve, _) => {
        validateEmail(JSON.stringify({ email: values.email }), {
          onSuccess: () => resolve(true),
          onError: (emailError) => {
            if (emailError?.json?.message && emailError.status === 400) {
              setServerEmailErrorMessages([
                emailError.json.message,
                loginMessage,
              ]);
              resetField('email', { defaultValue: getValues().email });
            }
            resolve(false);
          },
        });
      }),
      refetchStudent(),
    ]);

    const studentError = studentResults.error;

    if (
      studentError &&
      studentError?.json?.message &&
      (studentError?.status === 400 || studentError?.status === 422)
    ) {
      // 400でも422でも生徒氏名の下にエラーを表示
      resetField('code', { defaultValue: getValues().code });
      resetField('student_first_name_kana', {
        defaultValue: getValues().student_first_name_kana,
      });
      resetField('student_last_name_kana', {
        defaultValue: getValues().student_last_name_kana,
      });
      setServerStudentKanaErrorMessages([
        '入力された団体会員コードが使用できないか、団体会員コードと生徒氏名（かな）が一致しません',
        '内容に誤りがないかもう一度お確かめください',
        '内容に誤りがない場合には学校にお問い合わせください',
      ]);
      return;
    }

    const studentData = studentResults.data?.student;

    if (emailResults && studentData) {
      onSubmit(values, studentData as StudentInfo);
    }
  });

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={submitHandler}
      className={CSSModule.AccountRegistrationForm}
    >
      <h2 className={(CSSModule as Record<string, string>).SectionHeader}>
        生徒情報入力
      </h2>
      <FormGroup className={CSSModule.FormGroup}>
        <LabelWithStatus
          required
          valid={isCodeReadyForOk && !shouldKeepCodeRequired}
        >
          団体会員コード（10桁）
        </LabelWithStatus>
        <Field isFullWidth>
          <OrganizationMembershipCodeSplitInput
            value={codeValue}
            onChange={(value) => {
              setValue('code', value, {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
              });
            }}
            onBlur={() => {
              // フォーカスが外れた時点で10桁未満なら、以降10桁を入力し切るまで必須エラーを表示する。
              // 「使用できません」は入力時にスキーマ側で表示されるため、ここでは関与しない。
              if (
                getValues('code').replace(/\s/g, '').length !==
                ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS
              ) {
                setIsCodeRequiredErrorActive(true);
              }
            }}
            disabled={inputDisabled}
            name="code"
          />
          <ErrorList errors={filteredCodeErrors} />
          <ul className={CSSModule.Comment}>
            <li>
              <p>
                学校から配布された団体会員コードを<b>半角数字</b>
                で入力してください。
              </p>
            </li>
            <li>
              <p>
                団体会員コードは１つにつき１アカウントまで登録可能です。複数のアカウントを登録することはできません。
              </p>
            </li>
          </ul>
        </Field>
      </FormGroup>
      <FormGroup className={CSSModule.FormGroup}>
        <LabelWithStatus
          required
          valid={isStudentKanaReadyForOk && !shouldShowStudentKanaServerError}
        >
          生徒氏名（かな）
        </LabelWithStatus>
        <Field isFullWidth>
          <div className={CSSModule.FieldGroup__TwoLines}>
            <Field className={CSSModule.Field} isFullWidth>
              <Input
                type="text"
                {...registerWithInnerRef('student_last_name_kana')}
                disabled={inputDisabled}
                placeholder="例）やまだ"
              />
            </Field>
            <Field className={CSSModule.Field} isFullWidth>
              <Input
                type="text"
                {...registerWithInnerRef('student_first_name_kana')}
                disabled={inputDisabled}
                placeholder="例）はなこ"
              />
            </Field>
          </div>
          <ErrorList errors={studentNameKanaErrors} />
          {shouldShowStudentKanaServerError && (
            <ErrorList errors={serverStudentKanaErrorMessages} />
          )}
          <ul className={CSSModule.Comment}>
            <li>
              <p>
                登録されている生徒氏名（かな）が分からない、エラーが発生する場合は、
                <a
                  href="https://studysapuri-school.jp/studentfaq/s/article/000002480"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ヘルプページ
                </a>
                をご確認ください。
              </p>
            </li>
          </ul>
        </Field>
      </FormGroup>
      <h2
        className={
          (CSSModule as Record<string, string>).SectionHeaderWithBorder
        }
      >
        保護者情報入力
      </h2>
      <FormGroup className={CSSModule.FormGroup}>
        <LabelWithStatus
          required
          valid={validFields.first_name && validFields.last_name}
        >
          保護者氏名
        </LabelWithStatus>
        <Field isFullWidth>
          <div className={CSSModule.FieldGroup__TwoLines}>
            <Field className={CSSModule.Field} isFullWidth>
              <Input
                type="text"
                {...registerWithInnerRef('last_name')}
                disabled={inputDisabled}
                placeholder="例）山田"
              />
            </Field>
            <Field className={CSSModule.Field} isFullWidth>
              <Input
                type="text"
                {...registerWithInnerRef('first_name')}
                disabled={inputDisabled}
                placeholder="例）太郎"
              />
            </Field>
          </div>
          <ErrorList errors={nameErrors} />
        </Field>
      </FormGroup>
      <FormGroup className={CSSModule.FormGroup}>
        <LabelWithStatus
          required
          valid={validFields.first_name_kana && validFields.last_name_kana}
        >
          保護者氏名（かな）
        </LabelWithStatus>
        <Field isFullWidth>
          <div className={CSSModule.FieldGroup__TwoLines}>
            <Field className={CSSModule.Field} isFullWidth>
              <Input
                type="text"
                {...registerWithInnerRef('last_name_kana')}
                disabled={inputDisabled}
                placeholder="例）やまだ"
              />
            </Field>
            <Field className={CSSModule.Field} isFullWidth>
              <Input
                type="text"
                {...registerWithInnerRef('first_name_kana')}
                disabled={inputDisabled}
                placeholder="例）たろう"
              />
            </Field>
          </div>
          <ErrorList errors={nameKanaErrors} />
        </Field>
      </FormGroup>
      <FormGroup className={CSSModule.FormGroup}>
        <LabelWithStatus required valid={validFields.email}>
          メールアドレス
        </LabelWithStatus>
        <Field isFullWidth>
          <Input
            type="text"
            {...registerWithInnerRef('email')}
            disabled={inputDisabled}
            placeholder="メールアドレスを入力"
          />
          <ErrorList errors={emailErrors} />
          <CustomEmailErrorMessages />
          <ul className={CSSModule.Comment}>
            <li>
              <p>携帯会社のメールアドレスは受信できない場合があります。</p>
            </li>
            <li>
              <p>
                <b>PCのメールアドレスなどのご登録</b>を推奨します。
              </p>
            </li>
          </ul>
        </Field>
      </FormGroup>
      <FormGroup className={CSSModule.FormGroup}>
        <LabelWithStatus
          required
          valid={validFields.password && validFields.password_confirmation}
        >
          パスワード
        </LabelWithStatus>
        <Field isFullWidth>
          <PasswordInput
            {...registerWithInnerRef('password', {
              onChange: () => {
                if (
                  touchedFields.password_confirmation ||
                  formValues.password_confirmation
                ) {
                  void trigger('password_confirmation');
                }
              },
            })}
            disabled={inputDisabled}
            autoComplete="new-password"
            placeholder="パスワードを入力"
          />
        </Field>
        <Field isFullWidth>
          <PasswordInput
            {...registerWithInnerRef('password_confirmation')}
            disabled={inputDisabled}
            autoComplete="new-password"
            placeholder="パスワードを入力（確認用）"
          />

          <ErrorList errors={passwordErrors} />
          <ul className={CSSModule.Comment}>
            <li className={CSSModule.Comment__Warning}>
              <p>最低6文字以上にしてください。</p>
            </li>
            <li className={CSSModule.Comment__Warning}>
              <p>
                半角アルファベットと半角数字をそれぞれ1文字以上入れてください。
              </p>
            </li>
            <li>
              <p>
                名前＋生年月日などは推測されやすいため、英数字以外の記号組み合わせを推奨します。
              </p>
            </li>
          </ul>
        </Field>
      </FormGroup>
      <ServerErrorMessage />
      <RateLimitErrorMessage />
      <div className={CSSModule.SubmitButtonArea}>
        <Button
          className={CSSModule.SubmitButton}
          color="primary"
          type="submit"
          disabled={buttonDisabled}
        >
          確認画面へ
        </Button>
      </div>
    </form>
  );
};

export default AccountRegistrationForm;
